import { useState, useEffect } from 'react';

import { useMutation } from '@apollo/client';
import styled from '@emotion/styled';
import {
    colors, isValidPhone, formatPhone, trimPhone
} from '@modernanimal/common';
import * as Sentry from '@sentry/react';
import { useBreakpoint } from 'use-breakpoint';

import { formatRelativeAppointmentDatetime } from './clientOnboarding.helpers';
import { AUTHORIZE_PHONE } from './clientOnboarding.v3.gql';
import InputField from '../components/InputField';
import PrimaryButton from '../components/PrimaryButton';
import Fonts from '../fonts';
import { BREAKPOINTS } from '../helpers';
import Icons from '../icons';
import useWebBookingStore, { resetAllSlices, initialClientState } from '../store';

type Props = {
    goToNextStep: ()=> void;
};

const PhoneNumber = ({ goToNextStep }: Props) => {
    const {
        client, updateClient, appointment, updateAppointment
    } = useWebBookingStore((state) => ({
        client: state.client,
        updateClient: state.setClient,
        appointment: state.appointment,
        updateAppointment: state.setAppointment,
    }));
    const { breakpoint } = useBreakpoint(BREAKPOINTS, 'desktop');
    const isMobile = breakpoint === 'mobile';

    const [phoneNumber, setPhoneNumber] = useState(formatPhone(client.household?.id ? client.phoneNumber : ''));
    const [isValidPhoneNumber, setIsValidPhoneNumber] = useState(false);
    const [authorizePhone, { loading: loadingAuthorizePhone }] = useMutation(AUTHORIZE_PHONE);

    useEffect(() => {
        setIsValidPhoneNumber(isValidPhone(phoneNumber));
    }, [phoneNumber]);

    const changePhoneNumber = (phoneNumber: string) => {
        if (trimPhone(phoneNumber).length <= 10) {
            const formattedPhoneNumber = formatPhone(phoneNumber);
            setPhoneNumber((prevPhoneNumber) => {
                const updatedPhoneNumber = (prevPhoneNumber === formattedPhoneNumber
                    && phoneNumber.length < prevPhoneNumber.length)
                    ? phoneNumber
                    : formattedPhoneNumber;
                return updatedPhoneNumber;
            });
        }
    };

    const sendPhoneAuth = async () => {
        let updatedClient = client;

        // If the entered phone number is different from what's stored in the Zustand store,
        // reset the store so we're not using the same data for a different client
        if (client.phoneNumber && trimPhone(phoneNumber) !== client.phoneNumber) {
            updatedClient = initialClientState;
            resetAllSlices(['appointment']);
            // Clear reasonForVisitDescription separately since it's part of the appointment slice
            // but we don't want it to carry over to the next client.
            updateAppointment({ ...appointment, reasonForVisitDescription: '' });
        }

        await authorizePhone({
            variables: { phoneNumber },
            onCompleted(data) {
                updateClient({
                    ...updatedClient,
                    phoneNumber: trimPhone(phoneNumber)
                });
                goToNextStep();
            },
            onError: (error) => {
                Sentry.captureException(error, {
                    extra: {
                        clientId: client.id,
                        path: 'src/clientOnboarding/PhoneNumber',
                        function: 'authorizePhone'
                    },
                });
                // TODO: We just have a more UI-friendly way to handle errors
                // eslint-disable-next-line no-alert
                alert(`Error! ${error}`);
            }
        });
    };

    const renderAppointmentInfo = () => {
        const { slot } = appointment;
        if (!slot) return null;
        return (
            <AppointmentInfo data-testid="appointmentInfoCard">
                <Icons.Calendar width={24} height={24} fill={colors.GREY_1} />
                <Fonts.LabelMedium>
                    {`${formatRelativeAppointmentDatetime(slot.start, slot.location.region.timezone)},`}
                    <br />
                    {slot.location?.name}
                </Fonts.LabelMedium>
            </AppointmentInfo>
        );
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && isValidPhoneNumber && !loadingAuthorizePhone) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            sendPhoneAuth();
        }
    };

    return (
        <PhoneNumberContainer>
            <Body>
                {renderAppointmentInfo()}
                <Instructions>
                    <Fonts.Heading04>
                        Enter your phone number
                    </Fonts.Heading04>
                    <Fonts.Body style={{ color: colors.GREY_2 }}>
                        {'We\'ll text you a one-time verification code that helps confirm your identity.'}
                    </Fonts.Body>
                </Instructions>
                <PhoneInputContainer>
                    <CountryCode>US +1</CountryCode>
                    <InputField
                        placeholder="Phone number"
                        value={phoneNumber}
                        onChange={changePhoneNumber}
                        autoFocus={!phoneNumber}
                        inputMode="numeric"
                        onKeyDown={handleKeyPress}
                    />
                </PhoneInputContainer>
                <Fonts.BodySmall>
                    {`Messages and data rates may apply. To continue, you certify that you are 18
                    years of age or older, and you agree to the `}
                    <Link href="https://www.modernanimal.com/terms" target="_blank">
                        Terms and Conditions
                    </Link>
                    {' and '}
                    <Link href="https://www.modernanimal.com/privacy" target="_blank">
                        Privacy Policy
                    </Link>
                    {'.'}
                </Fonts.BodySmall>
            </Body>
            <NextButtonContainer isMobile={isMobile}>
                <PrimaryButton disabled={!isValidPhoneNumber || loadingAuthorizePhone} onClick={sendPhoneAuth} color="periwinkle" id="add-phone-number-button">
                    Next
                </PrimaryButton>
            </NextButtonContainer>
        </PhoneNumberContainer>
    );
};

export default PhoneNumber;

const PhoneNumberContainer = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: 32px 24px 0 24px;
    gap: 8px;
    justify-content: space-between;
`;

const AppointmentInfo = styled.div`
    display: flex;
    flex-direction: row;
    gap: 16px;
    padding: 12px 16px;
    align-items: center;
    border-radius: 8px;
    background-color: ${colors.PERIWINKLE_6};
    justify-content: center;
    width: 100%;
    box-sizing: border-box;
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    align-items: center;
`;

const CountryCode = styled(Fonts.Heading06)`
    color: ${colors.GREY_2};
    padding: 0px 8px;
    white-space: nowrap;
`;

const Instructions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
`;

const Link = styled.a`
    color: ${colors.PRIMARY_PERIWINKLE};
`;

const NextButtonContainer = styled.div<{ isMobile: boolean }>`
    display: flex;
    position: ${({ isMobile }) => (isMobile ? 'sticky' : 'static')};
    bottom: 0px;
    background-color: ${colors.WHITE};
    padding: 16px 0 32px 0;
`;

const PhoneInputContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 4px;
    align-items: center;
    width: 100%;
`;
